// PatientLayout.jsx
import { Container, Modal } from "react-bootstrap";
import { Link, Outlet, useLocation, useParams } from "react-router-dom";
import Breadcrumbs from "../../components/ui/Breadcrumbs";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getLocale } from "../../i18n/config";
import GetData from "../../services/getData";
import logPatientChange from "../../services/patientLogs";
import { buildPatientLogText } from "../../services/patientLogMessages";
import EditableModal from "../../components/layout/editableModal/EditableModal";
import "./PatientLayout.css";
import "./patientDocView.css";

const TAB_LABEL_KEYS = {
  overview: "layout.tabs.overview",
  contacts: "layout.tabs.contacts",
  profile: "layout.tabs.profile",
  analyses: "layout.tabs.analyses",
  inbody: "layout.tabs.inbody",
  dynamics: "layout.tabs.dynamics",
  diagnostics: "layout.tabs.diagnostics",
  documents: "layout.tabs.documents",
  plan: "layout.tabs.plan",
  "ai-analyses": "layout.tabs.ai-analyses",
  payments: "layout.tabs.payments",
  history: "layout.tabs.history",
  appointments: "layout.tabs.appointments",
  recommendations: "layout.tabs.recommendations",
};

const PROFILE_DOC_LABEL_KEYS = {
  primary: "layout.profileDocs.primary",
  repeat: "layout.profileDocs.repeat",
  anamnesis: "layout.profileDocs.anamnesis",
  anketa: "layout.profileDocs.anketa",
  "anketa-simple": "layout.profileDocs.anketa-simple",
  "smart-diagnostics": "layout.profileDocs.smart-diagnostics",
};

// Табы шапки карточки пациента (порядок = порядок кнопок).
const PATIENT_TAB_SECTIONS = [
  "overview",
  "profile",
  "diagnostics",
  "plan",
  "documents",
];

// Вложенные разделы → таб, к которому они относятся. Нужно, чтобы активный
// таб был жёлтым сразу при открытии страницы, даже если маршрут не совпадает
// с именем таба буквально.
const SECTION_PARENT_TAB = {
  contacts: "overview",
  analyses: "diagnostics",
  "ai-analyses": "diagnostics",
  inbody: "diagnostics",
  dynamics: "diagnostics",
  recommendations: "plan",
  appointments: "plan",
  payments: "profile",
  history: "profile",
};

const getTabLabel = (t, section) => {
  const key = TAB_LABEL_KEYS[section];
  return key ? t(key) : section;
};

const displayBriefValue = (value) => {
  if (value === 0) return "0";
  const text = String(value ?? "").trim();
  if (!text || text === "0000-00-00" || text === "0000-00-00 00:00:00") {
    return "—";
  }
  return text;
};

// Дата без возраста: «2026-10-21» → «21.10.2026»
const formatShortDate = (isoDate, locale) => {
  const raw = String(isoDate ?? "").trim();
  if (!raw || raw.startsWith("0000-00-00")) return "";
  const [datePart] = raw.split(/[ T]/);
  const [year, month, day] = datePart.split("-");
  if (!year || !month || !day) return "";

  const date = new Date(Number(year), Number(month) - 1, Number(day));
  if (Number.isNaN(date.getTime())) {
    return `${day}.${month}.${year}`;
  }

  return date.toLocaleDateString(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const getDoctorId = (patient) => {
  const doc = patient?.doc_name?.value;
  if (doc && typeof doc === "object" && doc.id) return String(doc.id);
  return "";
};

// Список имён лечащих врачей (может быть несколько)
const getDoctorList = (patient) => {
  const names = Array.isArray(patient?.doc_names?.value)
    ? patient.doc_names.value
        .map((docItem) =>
          typeof docItem === "string" ? docItem : docItem?.name,
        )
        .filter(Boolean)
    : [];
  if (names.length > 0) return names;

  const doc = patient?.doc_name?.value;
  if (!doc) return [];
  const single = typeof doc === "string" ? doc : doc.name;
  return single ? [single] : [];
};

// Инициалы для кружка-аватара: «Иванов Иван» → «ИИ»
const getInitials = (name) => {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

// Список id лечащих врачей (id_users приходит из patients_doctors; если связей
// нет — бэкенд отдаёт одиночный patients.id_user через doc_name).
const getDoctorIds = (patient) => {
  if (Array.isArray(patient?.id_users?.value)) {
    return patient.id_users.value
      .map((idItem) => String(idItem))
      .filter(Boolean);
  }
  const docId = getDoctorId(patient);
  return docId ? [docId] : [];
};

const normalizeBirthDateInput = (isoDate) => {
  const raw = String(isoDate ?? "").trim();
  if (!raw || raw.startsWith("0000-00-00")) return "";
  return raw.split(/[ T]/)[0];
};

const BRIEF_FIELD_LABEL_KEYS = {
  fio: "layout.fields.fio",
  phone: "layout.fields.phone",
  birthDate: "layout.fields.birthDate",
  gender: "layout.fields.gender",
  doc_ids: "layout.fields.doc_ids",
  id_clinic: "layout.fields.id_clinic",
  type: "layout.fields.type",
  mail: "layout.fields.mail",
  cart: "layout.fields.cart",
  comment: "layout.fields.comment",
};

const mapBriefFormData = (patient) => ({
  fio: patient?.fio?.value ?? "",
  phone: patient?.phone?.value ?? "",
  birthDate: normalizeBirthDateInput(patient?.birthDate?.value),
  gender: patient?.gender?.value ?? "",
  doc_id: getDoctorId(patient),
  doc_ids: getDoctorIds(patient),
  id_clinic: String(patient?.id_clinic?.value ?? ""),
  type: String(patient?.type?.value ?? ""),
  mail: patient?.mail?.value ?? "",
  cart: patient?.cart?.value ?? "",
  comment: patient?.comment?.value ?? "",
});

const normalizeComparableValue = (value) =>
  value == null ? "" : String(value).trim();

const collectChangedFields = (before, after, labelKeys, t) =>
  Object.keys(labelKeys).reduce((acc, key) => {
    const fromValue = normalizeComparableValue(before?.[key]);
    const toValue = normalizeComparableValue(after?.[key]);
    if (fromValue !== toValue) {
      acc.push({
        label: t(labelKeys[key]),
        from: before?.[key],
        to: after?.[key],
      });
    }
    return acc;
  }, []);

function getPlanIdFromPath(patientId, pathname) {
  const basePath = `/patient/${patientId}`;
  const relativePath = pathname.startsWith(basePath)
    ? pathname.slice(basePath.length).replace(/^\/+/, "")
    : "";
  const segments = relativePath ? relativePath.split("/").filter(Boolean) : [];

  if (segments[0] !== "plan" || !segments[1]) {
    return null;
  }

  return segments[1];
}

function buildPatientBreadcrumbs({
  patientId,
  patientName,
  planName,
  pathname,
  t,
}) {
  const basePath = `/patient/${patientId}`;
  const relativePath = pathname.startsWith(basePath)
    ? pathname.slice(basePath.length).replace(/^\/+/, "")
    : "";
  const segments = relativePath ? relativePath.split("/").filter(Boolean) : [];

  const items = [
    { label: t("layout.breadcrumbs.patients"), to: "/patient/" },
    {
      label: patientName || t("layout.breadcrumbs.patient"),
      to: `${basePath}/profile`,
    },
  ];

  if (!segments.length) {
    return items;
  }

  const [section, ...rest] = segments;
  const sectionLabel = getTabLabel(t, section);

  if (section === "plan") {
    items.push({
      label: sectionLabel,
      to: `${basePath}/plan`,
    });

    if (rest.length === 0) {
      return items;
    }

    const [planId, nestedSection] = rest;
    items.push({
      label: planName || t("layout.breadcrumbs.plan", { id: planId }),
      to: `${basePath}/plan/${planId}`,
    });

    if (nestedSection) {
      items.push({
        label: getTabLabel(t, nestedSection),
      });
    }

    return items;
  }

  if (section === "diagnostics") {
    items.push(
      rest.length
        ? { label: sectionLabel, to: `${basePath}/diagnostics` }
        : { label: sectionLabel },
    );
    if (rest[0] === "uzi") {
      items.push({
        label: t("layout.breadcrumbs.uziProtocol"),
      });
    }
    return items;
  }

  if (section === "profile") {
    const [docType] = rest;
    const docLabelKey = PROFILE_DOC_LABEL_KEYS[docType];

    if (docLabelKey) {
      items.push({
        label: sectionLabel,
        to: `${basePath}/profile`,
      });
      items.push({
        label: t(docLabelKey),
      });
      return items;
    }

    items.push({
      label: sectionLabel,
    });
    return items;
  }

  items.push({
    label: sectionLabel,
  });

  return items;
}

export default function PatientLayout() {
  const { t, i18n } = useTranslation("patient");
  const locale = getLocale(i18n.language);
  const { patientId } = useParams();
  const location = useLocation();
  const heroRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [patient, setPatient] = useState([]);
  const [planName, setPlanName] = useState("");
  const [showBriefEdit, setShowBriefEdit] = useState(false);
  const [showAiChat, setShowAiChat] = useState(false);
  const [patientVersion, setPatientVersion] = useState(0);
  const [planActionsSlotEl, setPlanActionsSlotEl] = useState(null);
  const [planGroupsSlotEl, setPlanGroupsSlotEl] = useState(null);

  const currentPlanId = useMemo(
    () => getPlanIdFromPath(patientId, location.pathname),
    [patientId, location.pathname],
  );

  useEffect(() => {
    getPatients(patientId);
  }, [patientId]);

  useEffect(() => {
    let cancelled = false;

    const loadPlanName = async () => {
      if (!patientId || !currentPlanId) {
        setPlanName("");
        return;
      }

      try {
        const data = await GetData({
          module: "patient",
          block: "treatment_plan_list",
          id_patient: patientId,
        });
        if (cancelled) return;

        const plans = data?.result || [];
        const matchedPlan = plans.find(
          (plan) => String(plan?.id) === String(currentPlanId),
        );
        setPlanName(matchedPlan?.name || "");
      } catch (err) {
        if (!cancelled) {
          setPlanName("");
        }
      }
    };

    loadPlanName();

    return () => {
      cancelled = true;
    };
  }, [patientId, currentPlanId]);

  const getPatients = async (id) => {
    try {
      const data = await GetData({
        module: "patient",
        block: "info",
        id_patient: id,
      });
      setPatient(data.result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
// При открытии карточки пациента страница всегда начинается сверху,
// иначе сохраняется прокрутка списка и верхний блок не виден.
useEffect(() => {
  if (typeof window === "undefined") return;
  if ("scrollRestoration" in window.history) {
    window.history.scrollRestoration = "manual";
  }
  window.scrollTo({ top: 0, left: 0, behavior: "auto" });
}, [patientId]);
  useEffect(() => {
    if (patient?.fio?.value) {
      document.title = patient.fio.value;
    }
  }, [patient]);

  const breadcrumbItems = useMemo(
    () =>
      buildPatientBreadcrumbs({
        patientId,
        patientName: patient?.fio?.value,
        planName,
        pathname: location.pathname,
        t,
      }),
    [patientId, patient?.fio?.value, planName, location.pathname, t],
  );

  // Активный раздел карточки пациента (по первому сегменту после /patient/:id)
  const currentSection = useMemo(() => {
    const basePath = `/patient/${patientId}`;
    const relative = location.pathname.startsWith(basePath)
      ? location.pathname.slice(basePath.length).replace(/^\/+/, "")
      : "";
    return relative.split("/").filter(Boolean)[0] || "overview";
  }, [patientId, location.pathname]);

  // Какой таб шапки считать активным. Разделов в маршрутах больше, чем табов
  // (анализы, инбади, динамика, назначения, рекомендации), поэтому «вложенные»
  // разделы относим к своему табу — иначе на такой странице не подсвечен ни один
  // таб, и жёлтый появляется только после клика.
  const activeTab = useMemo(() => {
    const direct = PATIENT_TAB_SECTIONS.includes(currentSection);
    if (direct) return currentSection;
    return SECTION_PARENT_TAB[currentSection] || "overview";
  }, [currentSection]);

  // Белая карточка-обёртка — для всех табов, кроме обзора (там свои блоки).
  // На печати её стили снимаются отдельными print-правилами.
  const wrapInBlock = currentSection !== "overview";

  const heroDoctors = useMemo(() => getDoctorList(patient), [patient]);

  // Дата последнего визита для бейджа над ФИО
  const lastVisitDate = useMemo(
    () => formatShortDate(patient?.last_visit?.value, locale),
    [patient?.last_visit?.value, locale],
  );

  // Параллакс подсветки в «герое» — следует за указателем
  const handleHeroMove = (event) => {
    const el = heroRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width - 0.5;
    const y = (event.clientY - rect.top) / rect.height - 0.5;
    el.style.setProperty("--gx", `${(x * 36).toFixed(1)}px`);
    el.style.setProperty("--gy", `${(y * 28).toFixed(1)}px`);
  };

  const handleHeroLeave = () => {
    const el = heroRef.current;
    if (!el) return;
    el.style.setProperty("--gx", "0px");
    el.style.setProperty("--gy", "0px");
  };

  const doctorOptions = useMemo(() => {
    const doctors = Array.isArray(patient?.all_doctors)
      ? patient.all_doctors
      : [];
    return doctors.map((doctor) => ({
      value: String(doctor?.id ?? ""),
      label: doctor?.name || "—",
    }));
  }, [patient?.all_doctors]);

  const clinicOptions = useMemo(() => {
    const clinics = Array.isArray(patient?.all_clinics)
      ? patient.all_clinics
      : [];
    return clinics.map((clinic) => ({
      value: String(clinic?.id ?? ""),
      label: clinic?.name || "—",
    }));
  }, [patient?.all_clinics]);

  const visitTypeOptions = useMemo(() => {
    const labels = patient?.visit_type_labels;
    if (labels && typeof labels === "object") {
      return Object.entries(labels).map(([value, label]) => ({
        value: String(value),
        label: String(label),
      }));
    }
    return [
      { value: "1", label: t("layout.visitTypes.clinic") },
      { value: "2", label: t("layout.visitTypes.online") },
    ];
  }, [patient?.visit_type_labels, t]);

  const briefEditFields = useMemo(
    () => [
      { name: "fio", label: t("layout.fields.fio"), type: "text", required: true },
      { name: "phone", label: t("layout.fields.phone"), type: "tel" },
      { name: "birthDate", label: t("layout.fields.birthDate"), type: "date" },
      {
        name: "gender",
        label: t("layout.fields.gender"),
        type: "select",
        options: [
          { value: "Мужской", label: t("layout.gender.male") },
          { value: "Женский", label: t("layout.gender.female") },
        ],
      },
      {
        name: "doc_ids",
        label: t("layout.fields.doc_ids"),
        type: "checkboxes",
        options: doctorOptions,
        hint: t("layout.doctorHint"),
      },
      {
        name: "id_clinic",
        label: t("layout.fields.id_clinic"),
        type: "select",
        options: clinicOptions,
      },
      {
        name: "type",
        label: t("layout.fields.type"),
        type: "select",
        options: visitTypeOptions,
      },
      { name: "mail", label: t("layout.fields.mail"), type: "email" },
      { name: "cart", label: t("layout.fields.cart"), type: "text" },
      {
        name: "comment",
        label: t("layout.fields.comment"),
        type: "textarea",
        rows: 4,
      },
    ],
    [doctorOptions, clinicOptions, visitTypeOptions, t],
  );

  const briefInitialData = useMemo(() => mapBriefFormData(patient), [patient]);

  const doctorNameById = (id) => {
    const match = doctorOptions.find(
      (option) => option.value === String(id ?? ""),
    );
    return match?.label || (id ? String(id) : "—");
  };

  const doctorNamesById = (ids) => {
    if (!Array.isArray(ids) || ids.length === 0) return "—";
    return ids.map((id) => doctorNameById(id)).join(", ");
  };

  const clinicNameById = (id) => {
    const match = clinicOptions.find(
      (option) => option.value === String(id ?? ""),
    );
    return match?.label || (id ? String(id) : "—");
  };

  const visitTypeLabelById = (id) => {
    const match = visitTypeOptions.find(
      (option) => option.value === String(id ?? ""),
    );
    return match?.label || (id ? String(id) : "—");
  };

  const handleSaveBrief = async (formData) => {
    const previous = mapBriefFormData(patient);
    try {
      const result = await GetData({
        module: "patient",
        block: "info",
        id_patient: patientId,
        action: "change",
        fio: formData.fio,
        phone: formData.phone,
        birthDate: formData.birthDate,
        gender: formData.gender,
        mail: formData.mail,
        cart: formData.cart,
        comment: formData.comment ?? "",
        id_clinic: formData.id_clinic || "",
        type: formData.type || "",
        id_user:
          (Array.isArray(formData.doc_ids) ? formData.doc_ids[0] : "") || "",
        id_users: JSON.stringify(formData.doc_ids || []),
      });

      if (!result || result instanceof Error || !result.result) {
        throw new Error(result?.message || t("layout.saveFailed"));
      }

      const namedPrevious = {
        ...previous,
        doc_ids: doctorNamesById(previous.doc_ids),
        id_clinic: clinicNameById(previous.id_clinic),
        type: visitTypeLabelById(previous.type),
      };
      const namedNext = {
        ...formData,
        doc_ids: doctorNamesById(formData.doc_ids),
        id_clinic: clinicNameById(formData.id_clinic),
        type: visitTypeLabelById(formData.type),
      };

      await logPatientChange({
        idPatient: patientId,
        text: buildPatientLogText("patient_contact_updated", {
          changes: collectChangedFields(
            namedPrevious,
            namedNext,
            BRIEF_FIELD_LABEL_KEYS,
            t,
          ),
        }),
      });

      await getPatients(patientId);
      setPatientVersion((version) => version + 1);
      return true;
    } catch (err) {
      console.error("Ошибка сохранения данных пациента:", err);
      return false;
    }
  };

  if (!patient) {
    return <div>{t("notFoundPatient")}</div>;
  }

  const tabs = PATIENT_TAB_SECTIONS.map((section) => ({
    section,
    label: getTabLabel(t, section),
  }));

  return (
    <Container fluid="md">
      <div
        ref={heroRef}
        className="card patient-hero"
        onPointerMove={handleHeroMove}
        onPointerLeave={handleHeroLeave}>
        <div className="patient-hero__glow" aria-hidden="true" />
        <div className="patient-hero__grid">
          <div className="patient-hero__left">
            {lastVisitDate ? (
              <span className="patient-hero__badge">
                <span className="dot dot--live" aria-hidden="true" />
                {t("layout.brief.lastVisit")} · {lastVisitDate}
              </span>
            ) : null}

            <h1 className="patient-hero__title">
              {displayBriefValue(patient?.fio?.value)}
            </h1>

            <div className="patient-hero__doctors">
              <span className="patient-hero__doctors-label">
                {t("layout.brief.doctor")}
              </span>
              <span className="patient-hero__doctors-list">
                {heroDoctors.length > 0 ? (
                  heroDoctors.map((name) => (
                    <span
                      className="patient-hero__doctor"
                      key={name}
                      title={name}>
                      {name}
                    </span>
                  ))
                ) : (
                  <span className="patient-hero__doctor">—</span>
                )}
              </span>
            </div>

            <div className="patient-hero__actions">
              {tabs.map((tab) => {
                const isActive = activeTab === tab.section;
                return (
                  <Link
                    key={tab.section}
                    to={`/patient/${patientId}/${tab.section}`}
                    aria-current={isActive ? "page" : undefined}
                    data-active={isActive ? "true" : undefined}
                    className={`ui-btn ui-btn--md ${
                      isActive ? "ui-btn--accent" : "ui-btn--glass"
                    }`}>
                    {tab.label}
                  </Link>
                );
              })}
            </div>
          </div>

          <div className="patient-hero__right">
            <div className="patient-hero__avatar" aria-hidden="true">
              {getInitials(patient?.fio?.value)}
            </div>
            {patient?.cart?.value ? (
              <div className="patient-hero__cart">№ {patient.cart.value}</div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Хлебные крошки — вне блоков, между «героем» и контентом */}
      <div className="patient-breadcrumbs">
        <Breadcrumbs items={breadcrumbItems} />
      </div>

      <div className="page-content patient-page-layout">
        <div className="patient-main-column">
          <div
            className={`tab-content patient-tab-body block${
              wrapInBlock ? " page-block" : ""
            }`}>
            <Outlet
              context={{
                patient,
                refreshPatient: () => getPatients(patientId),
                patientVersion,
                planActionsSlotEl,
                planGroupsSlotEl,
                onEditBrief: () => setShowBriefEdit(true),
                onOpenAi: () => setShowAiChat(true),
              }}
            />
          </div>
          <div ref={setPlanActionsSlotEl} className="patient-plan-actions-slot" />
          <div ref={setPlanGroupsSlotEl} className="patient-plan-groups-slot" />
        </div>
      </div>
          <EditableModal
            show={showBriefEdit}
            onHide={() => setShowBriefEdit(false)}
            title={t("layout.editTitle")}
            initialData={briefInitialData}
            fields={briefEditFields}
            onSave={handleSaveBrief}
          />
          <Modal
            show={showAiChat}
            onHide={() => setShowAiChat(false)}
            size="md"
            centered
            className="patient-ai-chat-modal">
            <Modal.Header closeButton>
              <Modal.Title>{t("layout.aiConsultant")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="patient-ai-chat-stub">
                {t("layout.aiStub")}
              </div>
              {/* {showAiChat ? <PatientAnalysesAI embedded /> : null} */}
            </Modal.Body>
          </Modal>
    </Container>
  );
}
